
import { Component, Prop, Vue, Watch } from 'vue-facing-decorator'
import { Action, State } from 'vuex-facing-decorator'
import GoodsItem from "./goods-item.vue";
import ApplicationItem from "./application-item.vue";
import viewPackTrackDialog from "./pack-track-dialog";
import { getUserCouponContent } from '@/common/coupon'
import { getPayTypeText } from '@/common/pay'

@Component({
  components: {
    GoodsItem,
    ApplicationItem
  }
})
export default class extends Vue {
  @Prop()
  modelValue: any;

  /**
   * 详情页复用本组件时传 true。名字沿用参考项目的叫法：
   * 那边这个开关同时管着付款单那块和这个「详情」入口，
   * 我们列表接口不返回 payList，所以这里实际只用来藏「详情」按钮
   */
  @Prop()
  hidePayList: boolean;

  @State("express", {
    namespace: "store"
  })
  expressList

  @Action("store/getExpress")
  getExpress

  created() {
    /** 包裹上只存了 expressId，快递公司名字得从这个列表里查 */
    this.getExpress();
  }

  /**
   * 没付款看应收（receiveAmount），付过款看实收（payAmount）：
   * 下单时 payAmount 还是 0，直接显示它的话未付款订单这一列全是 0。
   * 拿 payAmount > 0 当「已付款」比拿 status 判更准 ——
   * status=4（订单关闭）里既有没付过款的，也有付过再关的
   */
  get showAmount() {
    if (Number(this.modelValue.payAmount) > 0) {
      return this.modelValue.payAmount;
    }
    return this.modelValue.receiveAmount || 0;
  }

  /** 买家没填昵称就显示「未设置」，不然那一行是空的，看不出有没有买家 */
  get buyerName() {
    return (this.modelValue.userInfo && this.modelValue.userInfo.name) || "未设置";
  }

  /**
   * 买家手机号取 phone 不取 mobile：
   * UserInfoEntity 里两个字段注释都是「手机号」，但 mobile 后端从来没写过，
   * 注册/登录落库用的是 phone
   */
  get buyerMobile() {
    let info = this.modelValue.userInfo || {};
    return info.phone || info.mobile || "";
  }

  /**
   * 订单用的优惠券，没用到就不显示这一格。
   * mainInfo 后端还没 join，拿不到券名和类型，那就退回按实际优惠金额说
   * （券自己的 price 就是这单实际减掉的钱）—— 等 mainInfo 连上了自动切成券名 + 券面内容
   */
  get couponText() {
    let coupon = this.modelValue.couponInfo;
    if (!coupon) return "";
    let main = coupon.mainInfo || {};
    /** getUserCouponContent 认不出类型时会返回 '-'，那种情况也按金额说 */
    let content = main.type === undefined ? "" : getUserCouponContent(coupon);
    if (!content || content === "-") content = `减${coupon.price}元`;
    return [main.name, content].filter(Boolean).join(" ");
  }

  /** 省市区和详细地址是分开存的，拼起来才是一条完整地址 */
  get fullAddress() {
    let parts = [
      this.modelValue.province,
      this.modelValue.city,
      this.modelValue.county,
      this.modelValue.address,
    ].filter((item) => item);
    return parts.length ? parts.join(" ") : "-";
  }

  get expressType() {
    switch (this.modelValue.expressType) {
      case 0:
        return "无需配送";
      case 1:
        return "快递";
      case 2:
        return "到店自提";
    }
    return "";
  }
  get orderStatus() {
    switch (this.modelValue.status) {
      case 0:
        return "等待客户付款";
      case 1:
        return "等待发货";
      case 2:
        return "商家已发货";
      case 3:
        return "交易完成";
      case 4:
        return "订单关闭";
    }
    return "";
  }
  get payType() {
    return getPayTypeText(this.modelValue.payType);
  }

  async remake() {
    let val = await this.$inputContent({
      title: "卖家备注",
      value: this.modelValue.remake,
    });
    let { error, message } = await this.$post("/manager/order/remake", {
      id: this.modelValue.id,
      remake: val,
    });
    if (error) return this.$error(message);
    this.refresh();
  }
  refresh() {
    this.$emit("refresh");
  }
  async returnGoods() {
    await this.$returnGoods({
      id: this.modelValue.id,
    });
    this.refresh();
  }
  async modifyPrice() {
    await this.$modifyPrice({
      id: this.modelValue.id,
    });
    this.refresh();
  }
  async sendGoods() {
    await this.$sendGoods({
      id: this.modelValue.id,
    });
    this.refresh();
  }
  /**
   * 按 path 跳而不是 name：name 取的是后端菜单配的 aliasName，
   * 这边只拿得到路径，写死 name 就得跟后端始终保持一致
   */
  gotoDetail() {
    this.$goto({
      path: "/shop/order/order/detail",
      query: { id: this.modelValue.id },
    });
  }

  viewPackTrack(pack) {
    viewPackTrackDialog(pack, this.modelValue);
  }

  packType(type) {
    return type == 1 ? "快递" : "无需物流";
  }

  submitStatusText(status) {
    switch (status) {
      case 0:
        return "未提交";
      case 1:
        return "已提交";
      case 2:
        return "提交失败";
    }
    return "-";
  }

  expressName(id) {
    let item = (this.expressList?.list || []).find((it) => it.id == id);
    return item ? item.name : "";
  }

  /** 省市区和详细地址分开存的，拼起来才是一条完整地址 */
  senderAddress(pack) {
    return [pack.senderProvince, pack.senderCity, pack.senderCounty, pack.senderAddress].filter(Boolean).join("");
  }

  receiverAddress(pack) {
    return [pack.receiverProvince, pack.receiverCity, pack.receiverCounty, pack.receiverAddress].filter(Boolean).join("");
  }

  async cancelPack(pack) {
    try {
      await this.$confirm("确定取消该包裹吗？已提交的电子面单会同步撤销。", {
        title: "取消包裹",
        type: "warning",
        confirmButtonText: "确定",
        cancelButtonText: "取消",
      });
    } catch (e) {
      /** 点了取消，什么都不做 */
      return;
    }
    let { error, message } = await this.$post("/manager/order/cancel-pack", {
      id: pack.id,
    });
    if (error) return this.$error(message);
    this.$success("取消成功");
    this.refresh();
  }
}
